// frontend/packages/business-console/src/pages/OutboxDrafts.jsx
//
// Work item 2.19 — pending / failed drafts list. Mirrored at
// /business/:slug/quotes/drafts and /business/:slug/invoices/drafts.
//
// Lets a field rep confirm "did my 3 drafts from this morning make it?"
// or fix one that failed with a 4xx (validation rejection). Synced
// rows are pruned after 5 minutes so this view is mostly empty in
// steady state.

import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import CloudSyncOutlinedIcon from '@mui/icons-material/CloudSyncOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

import { BusinessContext } from '../contexts/BusinessContext';
import useOnlineStatus from '../hooks/useOnlineStatus';
import { listAll, remove, put } from '../lib/outboxStore';
import { createQuoteDraft, updateQuoteDraft } from '../services/quoteService';
import {
  createInvoiceDraft,
  updateInvoiceDraft,
} from '../services/invoiceService';
import { submitWorkflowForm } from '../services/workflowFormService';

const KIND_LABEL = {
  quote: { plural: 'quotes', single: 'quote' },
  invoice: { plural: 'invoices', single: 'invoice' },
  // 5.2 — workflow-form submissions queued offline. Plural is
  // 'forms' on the URL (matches the planned 4.6 Forms list page) but
  // the user-facing label says "workflow form" in full to avoid
  // confusion with the public contact-form pipeline (D19).
  'workflow-form': { plural: 'forms', single: 'workflow form' },
};

const STATUS_COLOR = {
  pending: 'warning',
  syncing: 'info',
  failed: 'error',
  synced: 'success',
};

function describeRow(item) {
  const p = item.payload || {};
  const liCount = Array.isArray(p.lineItems) ? p.lineItems.length : 0;
  const titleBit = p.title ? ` · ${p.title}` : '';
  return `${liCount} line item${liCount === 1 ? '' : 's'}${titleBit}`;
}

function formatWhen(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString();
}

async function syncOne(item) {
  const targetId = item.serverId || null;
  if (item.kind === 'quote') {
    return targetId
      ? updateQuoteDraft(targetId, item.payload, {
          idempotencyKey: item.clientUuid,
        })
      : createQuoteDraft(item.payload, {
          idempotencyKey: item.clientUuid,
        });
  }
  if (item.kind === 'invoice') {
    return targetId
      ? updateInvoiceDraft(targetId, item.payload, {
          idempotencyKey: item.clientUuid,
        })
      : createInvoiceDraft(item.payload, {
          idempotencyKey: item.clientUuid,
        });
  }
  if (item.kind === 'workflow-form') {
    // Workflow-form submissions are append-only — no PATCH path,
    // always POST against the formIdOrSlug. The payload carries the
    // server-side `clientUuid` (same value as item.clientUuid), and
    // the route accepts `Idempotency-Key` for the in-flight Redis
    // layer. The `formIdOrSlug` lives on `payload.formIdOrSlug`,
    // carried by the renderer (or 5.2's caller) at enqueue time.
    const formIdOrSlug = item.payload?.formIdOrSlug;
    if (!formIdOrSlug) {
      throw new Error(
        'workflow-form outbox row is missing payload.formIdOrSlug'
      );
    }
    const { formIdOrSlug: _drop, ...rest } = item.payload;
    return submitWorkflowForm(formIdOrSlug, rest, {
      idempotencyKey: item.clientUuid,
    });
  }
  throw new Error(`Unknown outbox kind: ${item.kind}`);
}

export default function OutboxDrafts({ kind }) {
  const { business } = useContext(BusinessContext);
  const navigate = useNavigate();
  const { slug } = useParams();
  const basePath = business?.slug
    ? `/business/${business.slug}`
    : slug
      ? `/business/${slug}`
      : '';
  const online = useOnlineStatus();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retryingFor, setRetryingFor] = useState(null);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const all = await listAll({ kind });
      // Show pending + syncing + failed + recently-synced.
      const filtered = all
        .filter(
          (it) =>
            it.status !== 'synced' ||
            (it.savedAt || 0) > Date.now() - 5 * 60 * 1000
        )
        .sort((a, b) => (b.savedAt || 0) - (a.savedAt || 0));
      setItems(filtered);
    } finally {
      setLoading(false);
    }
  }, [kind]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleRetry = async (item) => {
    if (!online) {
      setError("You're offline. Reconnect and we'll retry automatically.");
      return;
    }
    setError(null);
    setRetryingFor(item.clientUuid);
    try {
      await put({ ...item, status: 'syncing' });
      const response = await syncOne(item);
      const doc = response?.quote || response?.invoice || response?.submission;
      await put({
        ...item,
        status: 'synced',
        serverId: doc?._id || item.serverId || null,
        serverNumber: doc?.number || item.serverNumber || null,
        savedAt: Date.now(),
        lastError: null,
        attemptCount: (item.attemptCount || 0) + 1,
      });
    } catch (err) {
      const httpStatus = err?.response?.status;
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Retry failed.';
      const isFatal = httpStatus && httpStatus >= 400 && httpStatus < 500;
      await put({
        ...item,
        status: isFatal ? 'failed' : 'pending',
        attemptCount: (item.attemptCount || 0) + 1,
        lastError: message,
      });
      setError(message);
    } finally {
      setRetryingFor(null);
      await refresh();
    }
  };

  const handleDelete = async (item) => {
    const ok = window.confirm(
      'Drop this draft from the outbox? Local changes will be lost.'
    );
    if (!ok) return;
    await remove(item.clientUuid);
    await refresh();
  };

  const handleOpen = (item) => {
    if (item.serverId) {
      navigate(`${basePath}/${KIND_LABEL[kind].plural}/${item.serverId}/edit`);
    } else {
      // No server id yet → can only re-open the New flow. The draft
      // body is in the outbox, not in URL state, so the operator may
      // need to manually re-enter values until we add hydrate-from-
      // outbox to the editor.
      navigate(`${basePath}/${KIND_LABEL[kind].plural}/new`);
    }
  };

  const label = KIND_LABEL[kind];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 960, mx: 'auto' }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <Tooltip title={`Back to ${label.plural}`}>
          <IconButton
            size="small"
            component={RouterLink}
            to={`${basePath}/${label.plural}`}
            sx={{ color: 'text.secondary' }}
            aria-label={`Back to ${label.plural}`}
          >
            <ArrowBackRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Typography sx={{ fontWeight: 700, color: 'text.primary', flex: 1 }}>
          Offline drafts ({label.plural})
        </Typography>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={refresh} aria-label="Refresh">
            <RefreshOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      {!online ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You&apos;re offline. Pending drafts will sync automatically when you
          reconnect.
        </Alert>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : items.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}
        >
          <Typography sx={{ color: 'text.secondary' }}>
            No pending or failed {label.plural}. Anything you save offline will
            land here until it syncs.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={1.5}>
          {items.map((item) => (
            <Paper
              key={item.clientUuid}
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                borderColor:
                  item.status === 'failed' ? 'error.light' : 'divider',
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 0.5 }}
                  >
                    <Chip
                      size="small"
                      label={item.status}
                      color={STATUS_COLOR[item.status] || 'default'}
                      variant={item.status === 'failed' ? 'filled' : 'outlined'}
                      sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: 'text.primary' }}
                    >
                      {item.serverNumber ||
                        (item.serverId ? 'Existing draft' : 'New draft')}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary' }}
                    >
                      · {describeRow(item)}
                    </Typography>
                  </Stack>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', display: 'block' }}
                  >
                    Saved {formatWhen(item.savedAt)}
                    {item.attemptCount > 0
                      ? ` · ${item.attemptCount} attempt${
                          item.attemptCount === 1 ? '' : 's'
                        }`
                      : ''}
                  </Typography>
                  {item.lastError ? (
                    <Typography
                      variant="caption"
                      sx={{ color: 'error.main', display: 'block', mt: 0.5 }}
                    >
                      {item.lastError}
                    </Typography>
                  ) : null}
                </Box>
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title="Open in editor">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => handleOpen(item)}
                        aria-label="Open"
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={online ? 'Retry sync now' : 'Reconnect to retry'}
                  >
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => handleRetry(item)}
                        disabled={
                          !online ||
                          retryingFor === item.clientUuid ||
                          item.status === 'syncing'
                        }
                        aria-label="Retry"
                      >
                        {retryingFor === item.clientUuid ? (
                          <CircularProgress size={16} />
                        ) : (
                          <CloudSyncOutlinedIcon fontSize="small" />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Discard local copy">
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(item)}
                      aria-label="Discard"
                      color="error"
                    >
                      <DeleteOutlineRoundedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      <Button
        component={RouterLink}
        to={`${basePath}/${label.plural}/new`}
        size="small"
        sx={{ mt: 3 }}
      >
        Start a new {label.single}
      </Button>
    </Box>
  );
}

export function QuoteDrafts() {
  return <OutboxDrafts kind="quote" />;
}

export function InvoiceDrafts() {
  return <OutboxDrafts kind="invoice" />;
}

export function WorkflowFormDrafts() {
  return <OutboxDrafts kind="workflow-form" />;
}
